import React, { Dispatch, SetStateAction } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../api/UserApi";
import { setAccessToken } from "../axioxClient.ts/axiosClient";
import { useUser } from "../context/UserContext";
import { dynamicFailed, dynamicSuccess } from "../hooks/useToast";
import { useLoader } from "../context/LoaderContext";
import { Icon } from "@iconify/react";
import { useSettings } from "../context/SettingsContext";
import { useRole } from "../hooks/useRole";
import { usePermission } from "../hooks/usePermission";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
}: {
  sidebarOpen: boolean;
  setSidebarOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const location = useLocation();
  const { generalSettings } = useSettings();
  const navigate = useNavigate();
  const { setUser } = useUser();
  const { isSuperAdmin } = useRole();
  const { canView } = usePermission();
  const { setLoading } = useLoader();
  const showAdminSection =
    isSuperAdmin ||
    canView("Users") ||
    canView("Settings") ||
    canView("Admin Control");
  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    if (path === "/cooperatives-list") {
      return (
        location.pathname.includes("/cooperatives-list") ||
        location.pathname.includes("/register/edit")
      );
    }

    return location.pathname.includes(path.replace("/", ""));
  };
  const handleClose = (path?: string) => {
    if (path && location.pathname === path) {
      setSidebarOpen(false);
      return;
    }

    setLoading(true);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      navigate("/login");
      dynamicSuccess("Logout successfully!");

      setTimeout(() => {
        setAccessToken(null);
        setUser(null);
        setLoading(false);
      }, 1000);
    } catch (error) {
      dynamicFailed("Logout successfully!");
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`sidebar ${sidebarOpen ? "open" : ""} d-flex flex-column`}
      >
        <button
          className="btn hamburger-icon p-0 text-end position-absolute top-0 end-0 m-2 z-3"
          onClick={() => setSidebarOpen(false)}
        >
          <i className="fa-solid fa-x"></i>
        </button>
        {/* Logo */}

        <div className="logo px-3 py-2 d-flex align-items-end gap-2">
          <img
            src={`${process.env.REACT_APP_Url}/${generalSettings?.documentUrl}`}
            alt="logo"
            className="logo-img"
          />
          <div className="logo-text">
            <h6 className="mb-0" style={{ fontSize: "15px" }}>Lalitpur Metropolitan City</h6>
            <small style={{ fontSize: "10px" }}>
              Cooperative Management System
            </small>
          </div>
        </div>

        {/* Menu */}
        <div className="menu flex-grow-1">
          {canView("dashboard") && (
            <h6
              className={`menu-title mt-3 ${isActive("/") ? "active" : ""}`}
              style={{ marginBottom: "35px" }}
            >
              <Link
                to="/"
                className="text-decoration-none"
                onClick={() => handleClose("/")}
              >
                <i className="fa-solid fa-chart-line me-2"></i>Dashboard
              </Link>
            </h6>
          )}

          <h6 className="section-title mb-2 mt-4">REGISTRY</h6>
          <ul>
            {canView("cooperatives") && (
              <li className={isActive("/cooperatives-list") ? "active" : ""}>
                <Link
                  to="/cooperatives-list"
                  className="text-decoration-none"
                  onClick={() => handleClose("/cooperatives-list")}
                >
                  <i className="fa-solid fa-user-group me-2"></i>Cooperative
                </Link>
              </li>
            )}
            {/* <li className={isActive("/applications") ? "active" : ""}>
              <Link
                to="/applications"
                className="text-decoration-none"
                onClick={() => handleClose("/applications")}
              >
                <i className="me-2">
                  <Icon
                    icon="tabler:file-download-filled"
                    className="sidebar-icon"
                  />
                </i>
                Applications
              </Link>
            </li> */}
            <li className={isActive("/register-new") ? "active" : ""}>
              <Link
                to="/register-new"
                className="text-decoration-none"
                onClick={() => handleClose("/register-new")}
              >
                <i className="me-2">
                  <Icon icon="mdi:register" className="sidebar-icon" />
                </i>
                Register New
              </Link>
            </li>
          </ul>
          {(canView("complaints") || canView("notices")) && (
            <>
              <h6 className="section-title mb-2 mt-4">GOVERNANCE</h6>
              <ul>
                {canView("complaints") && (
                  <li className={isActive("/complaints") ? "active" : ""}>
                    <Link
                      to="/complaints"
                      className="text-decoration-none"
                      onClick={() => handleClose("/complaints")}
                    >
                      <i className="fa-solid fa-circle-exclamation me-2"></i>
                      Complaints
                    </Link>
                  </li>
                )}
                {canView("notices") && (
                  <li className={isActive("/notices") ? "active" : ""}>
                    <Link
                      to="/notices"
                      className="text-decoration-none"
                      onClick={() => handleClose("/notices")}
                    >
                      <i className="fa-solid fa-bell me-2"></i>Official Notice
                    </Link>
                  </li>
                )}
              </ul>
            </>
          )}
          {showAdminSection && (
            <>
              <h6 className="section-title mb-2 mt-4">ADMINISTRATION</h6>
              <ul>
                {(isSuperAdmin || canView("Users")) && (
                  <li className={isActive("/user") ? "active" : ""}>
                    <Link
                      to="/user"
                      className="text-decoration-none"
                      onClick={() => handleClose("/user")}
                    >
                      <i className="fa-solid fa-user me-2"></i>User Management
                    </Link>
                  </li>
                )}
                {(isSuperAdmin || canView("Settings")) && (
                  <li className={isActive("/settings") ? "active" : ""}>
                    <Link
                      to="/settings"
                      className="text-decoration-none"
                      onClick={() => handleClose("/settings")}
                    >
                      <i className="me-2">
                        <Icon
                          icon="weui:setting-filled"
                          className="sidebar-icon"
                        />
                      </i>
                      Application Settings
                    </Link>
                  </li>
                )}
                {(isSuperAdmin || canView("Admin Control")) && (
                  <li className={isActive("/admin") ? "active" : ""}>
                    <Link
                      to="/admin"
                      className="text-decoration-none"
                      onClick={() => handleClose("/admin")}
                    >
                      <i className="fa-solid fa-sliders me-2"></i>Admin Panel
                    </Link>
                  </li>
                )}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sidebar-footer p-3">
          <span
            className="signup-link text-decoration-none"
            onClick={() => {
              handleClose();
              handleLogout();
            }}
          >
            <i className="fa-solid fa-right-from-bracket me-2"></i>
            <span>Logout</span>
          </span>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
